import { ref, type Ref } from "vue";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetCustomerCode,
} from "@/common/localStorageUtil";
import {
  ListDeliveryAreaQuery,
  ListDeliveryAreaQueryVariables,
  ListDeliveryAreaDocument,
  GetDeliveryGroupQuery,
  GetDeliveryGroupQueryVariables,
  GetDeliveryGroupDocument,
  GetDeliveryTimeQuery,
  GetDeliveryTimeQueryVariables,
  GetDeliveryTimeDocument,
  DeliveryGroupGetInput,
} from "@/generated/graphql";
import { ApolloQueryResult } from "@apollo/client/core";
import type {
  ListGroupDeliveryId,
  ListGroupDeliveryIdItem,
  AreaListItem,
  ChainSelectValue,
  DeliveryGroupSelectValue,
} from "../types";
import { GROUP_DELIVERY_ITEMS } from "../constants";

interface UseDeliverySettingGroupListParams {
  chainSelectValues: Ref<ChainSelectValue>;
  deliveryGroupSelectValues: Ref<DeliveryGroupSelectValue>;
  deliveryGroupGetInput: DeliveryGroupGetInput;
  listGroupDeliveryIdDataArray: Ref<ListGroupDeliveryId[]>;
  areaListItems: Ref<AreaListItem[]>;
  shopListItems: Ref<({ __typename?: "ShopResponse"; shop_code: string; name: string } | null | undefined)[]>;
  getAreaName: (area_code: string) => string;
  getShopName: (shop_code: string) => string;
  getDeliveryIdName: (delivery_id: number) => string;
}

/**
 * グループ一覧データの取得・整形処理を提供する composable。
 *
 * @returns 画面ロジックで利用する状態・関数
 */
export const useDeliverySettingGroupList = ({
  chainSelectValues,
  deliveryGroupSelectValues,
  deliveryGroupGetInput,
  listGroupDeliveryIdDataArray,
  areaListItems,
  shopListItems,
  getAreaName,
  getShopName,
  getDeliveryIdName,
}: UseDeliverySettingGroupListParams) => {
  const listGroupDeliveryId = ref<ListGroupDeliveryIdItem[]>([]);

  /**
   * 配達グループボックス @change
   */
  const onChangeGroup = async (): Promise<void> => {
    try {
      // graphql接続
      const apolloClient = await generateApolloClient();
      // グループ便一覧取得
      deliveryGroupGetInput.chain_code = chainSelectValues.value.chain_code;
      // deliveryGroupSelectValuesがnullの場合は処理をスキップ
      if (!deliveryGroupSelectValues.value?.group_code) {
        return;
      }
      deliveryGroupGetInput.group_code = deliveryGroupSelectValues.value.group_code;
      delete deliveryGroupGetInput["group_delivery_id"];
      let groupDeliveryid: ApolloQueryResult<GetDeliveryGroupQuery>;
      groupDeliveryid = await apolloClient.query<GetDeliveryGroupQuery, GetDeliveryGroupQueryVariables>({
        query: GetDeliveryGroupDocument,
        variables: { input: deliveryGroupGetInput },
        fetchPolicy: "no-cache",
      });

      if (
        groupDeliveryid.data.getDeliveryGroup &&
        groupDeliveryid.data.getDeliveryGroup.response &&
        groupDeliveryid.data.getDeliveryGroup.response.length > 0
      ) {
        listGroupDeliveryId.value = groupDeliveryid.data.getDeliveryGroup.response;
        listGroupDeliveryIdDataArray.value = [];

        // グループ便一覧作成
        for (const value of listGroupDeliveryId.value) {
          if (!value?.assigned_id_list) continue;

          // 対象エリア・便を加工
          const assigned_id_list_array: string[] = [];
          let delivery_id_label = "";

          // 非同期処理を並列実行して完了を待つ
          await Promise.all(
            value.assigned_id_list.map(async (_value) => {
              // エリアデータ取得処理
              const listArea = await apolloClient.query<ListDeliveryAreaQuery, ListDeliveryAreaQueryVariables>({
                query: ListDeliveryAreaDocument,
                variables: {
                  chain_code: chainSelectValues.value.chain_code,
                  customer_code: localStorageGetCustomerCode(),
                  shop_code: _value.shop_code,
                },
                fetchPolicy: "no-cache",
              });
              areaListItems.value = listArea.data.listDeliveryArea;
              const area_name = getAreaName(_value.area_code);

              // 配達時間取得
              const deliveryTimeInput = {
                chain_code: chainSelectValues.value.chain_code,
                customer_code: localStorageGetCustomerCode(),
                shop_code: _value.shop_code,
                area_code: _value.area_code,
                delivery_id: _value.delivery_id,
              };

              try {
                const delivery_time_data = await apolloClient.query<GetDeliveryTimeQuery, GetDeliveryTimeQueryVariables>({
                  query: GetDeliveryTimeDocument,
                  variables: { input: deliveryTimeInput },
                  fetchPolicy: "no-cache",
                });

                // 配達時間セット
                const deliveryTimeData = delivery_time_data.data.getDeliveryTime;
                if (deliveryTimeData && deliveryTimeData.delivery_start_time && deliveryTimeData.delivery_end_time) {
                  const deliveryStartTimeAry = deliveryTimeData.delivery_start_time.split(":");
                  const deliveryEndTimeAry = deliveryTimeData.delivery_end_time.split(":");

                  assigned_id_list_array.push(
                    getShopName(_value.shop_code) +
                    area_name +
                    getDeliveryIdName(_value.delivery_id) +
                    deliveryStartTimeAry[0].padStart(2, "0") +
                    ":" +
                    deliveryStartTimeAry[1].padStart(2, "0") +
                    "~" +
                    deliveryEndTimeAry[0].padStart(2, "0") +
                    ":" +
                    deliveryEndTimeAry[1].padStart(2, "0") +
                    "\r\n"
                  );
                }
              } catch (deliveryTimeErr) {
                // getDeliveryTimeがnullを返す場合はスキップ
                console.warn("配達時間の取得に失敗しました:", deliveryTimeErr);
              }
            })
          );

          // 配達グループ便ラベル取得
          for (var deliveryidkey in GROUP_DELIVERY_ITEMS) {
            if (GROUP_DELIVERY_ITEMS[deliveryidkey].delivery_id === value.group_delivery_id) {
              delivery_id_label = GROUP_DELIVERY_ITEMS[deliveryidkey].label;
              break;
            }
          }

          listGroupDeliveryIdDataArray.value.push({
            name: value.name,
            group_delivery_id: value.group_delivery_id.toString(),
            group_delivery_id_name: delivery_id_label,
            assigned_id_list: assigned_id_list_array,
            max_deliverable_unit: value.max_deliverable_unit,
            group_cap_mon: value.group_cap_mon,
            group_cap_tue: value.group_cap_tue,
            group_cap_wed: value.group_cap_wed,
            group_cap_thu: value.group_cap_thu,
            group_cap_fri: value.group_cap_fri,
            group_cap_sat: value.group_cap_sat,
            group_cap_sun: value.group_cap_sun,
            group_code: value.group_code,
          });
        }
      }
    } catch (err: unknown) {
      console.error(err);
      return;
    }
  };

  return {
    listGroupDeliveryId,
    onChangeGroup,
  };
};
